import React, { useState, useEffect } from "react";
import { endPoints, DefaultImageBaseurl } from "../../constant/Environment";
import { deleteData, getAllData } from "../../Utility/API";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { Delete } from "@mui/icons-material";
import { Button } from "react-bootstrap";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { AiOutlineEdit } from "react-icons/ai";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CreateTournament from "./CreateTournament";
import TournamentGamesModal from "./TournamentGamesModal";
import TournamentPlayersModal from "./TournamentPlayersModal";
import TournamentGuide from "./TournamentGuide";
import { tokens } from "../../theme";

/** A tournament whose results are saved, or whose end date has passed. */
const hasEnded = (row) => row.status !== "OPEN" || new Date(row.endDate).getTime() <= Date.now();

const PAST_PER_PAGE = 8;

/** Shown in the Status column: PAID wins, otherwise it follows the dates. */
const statusLabel = (row) => {
  if (row.status === "PAID") return "Paid";
  if (row.status === "FINISHED") return "Finished - results saved";
  if (row.isActive !== 1) return "Inactive";
  const now = Date.now();
  if (new Date(row.startDate).getTime() > now) return "Upcoming";
  if (new Date(row.endDate).getTime() > now) return "Live";
  return "Ended";
};

const Tournament = () => {
  const [currentRows, setCurrentRows] = useState([]);
  const [pastRows, setPastRows] = useState([]);
  const [pastTotal, setPastTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [gamesOpen, setGamesOpen] = useState(false);
  const [playersOpen, setPlayersOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pastPage, setPastPage] = useState(1);

  // Running and upcoming ones are always listed; past ones come from the server 8 at a time.
  const pastPageCount = Math.ceil(pastTotal / PAST_PER_PAGE);
  const rows = [...currentRows, ...pastRows];

  useEffect(() => {
    fetchTournaments();
  }, [pastPage]);

  const fetchTournaments = async () => {
    setIsLoading(true);
    const [current, past] = await Promise.all([
      getAllData(`${endPoints.api.TOURNAMENT_ADMIN}/current`),
      getAllData(`${endPoints.api.TOURNAMENT_ADMIN}/past?page=${pastPage}&size=${PAST_PER_PAGE}`),
    ]);
    setIsLoading(false);
    if (current?.status === "success") {
      setCurrentRows(current.data || []);
    }
    if (past?.status === "success") {
      const pastPageRows = past.data?.rows || [];
      // After a delete the last page can be empty, so step back to one that has rows.
      if (pastPageRows.length === 0 && pastPage > 1) {
        setPastPage(pastPage - 1);
        return;
      }
      setPastRows(pastPageRows);
      setPastTotal(past.data?.total || 0);
    }
  };

  const onCreateClose = () => {
    setCreateOpen(false);
    fetchTournaments();
  };

  const onPlayersClose = () => {
    setPlayersOpen(false);
    fetchTournaments();
  };

  /** Also removes its games and joined players; the backend refuses once prizes are paid. */
  const deleteTournament = async (row) => {
    const confirmed = window.confirm(
      `Delete "${row.name}"? Its games and joined players are removed too. This cannot be undone.`
    );
    if (!confirmed) return;
    setIsLoading(true);
    const response = await deleteData(`${endPoints.api.DELETE_TOURNAMENT}/${row.id}`);
    setIsLoading(false);
    if (response?.data?.status === "success") {
      SuccesToast(response.data.message);
      fetchTournaments();
    } else {
      ErrorToast(response?.data?.message || "Unable to delete tournament.");
    }
  };

  return (
    <div className="main">
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}
      <div id="Sportbook" style={{ padding: "0px" }}>
        <div className="container-fluid bg-color-sportbook custom-card4">
          <div className="inner-space1">
            <div className="row">
              <div
                className="col-md-12"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #444",
                }}
              >
                <h3 className="mb-2" style={{ fontFamily: "ArticulatCF-Bold" }}>
                  Tournaments
                </h3>
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <TournamentGuide />
                <Button
                  variant="primary"
                  className="mrglft10"
                  style={{ height: "fit-content" }}
                  onClick={() => {
                    setEditData({});
                    setCreateOpen(true);
                  }}
                >
                  Add Tournament
                </Button>
                </div>
              </div>
            </div>

            <CreateTournament
              open={createOpen}
              onClose={onCreateClose}
              editData={editData}
              // Every tournament can be edited, even after payout.
              // readOnly={!!editData?.id && editData.status !== "OPEN"}
            />

            <TournamentGamesModal
              open={gamesOpen}
              onClose={() => setGamesOpen(false)}
              tournament={selected}
            />

            <TournamentPlayersModal
              open={playersOpen}
              onClose={onPlayersClose}
              tournament={selected}
            />

            <div className="container-fluid table-custom">
              <div className="row" style={{ padding: "5px 0px 20px 0px" }}>
                <div className="col-md-12">
                  <TableContainer component={Paper}>
                    <Table className="c_table" size="small" stickyHeader sx={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Banner</TableCell>
                          <TableCell align="center">Name</TableCell>
                          {/* <TableCell align="center">Provider</TableCell> */}
                          <TableCell align="center">Win Mechanic</TableCell>
                          <TableCell align="center">Start</TableCell>
                          <TableCell align="center">End</TableCell>
                          <TableCell align="center">Prize Pool</TableCell>
                          <TableCell align="center">Winners</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.length === 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={9}>
                              No tournaments yet.
                            </TableCell>
                          </TableRow>
                        )}
                        {rows.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell align="center">
                              {row.bannerUrl ? (
                                <img
                                  src={`${DefaultImageBaseurl}${row.bannerUrl}`}
                                  alt={row.name}
                                  style={{ width: "90px", borderRadius: "6px" }}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="center">{row.name}</TableCell>
                            {/* <TableCell align="center">{row.provider}</TableCell> */}
                            <TableCell align="center">{row.winMechanic || "-"}</TableCell>
                            <TableCell align="center">
                              {row.startDate ? new Date(row.startDate).toLocaleString() : ""}
                            </TableCell>
                            <TableCell align="center">
                              {row.endDate ? new Date(row.endDate).toLocaleString() : ""}
                            </TableCell>
                            <TableCell align="center">{row.prizePool ?? "-"}</TableCell>
                            <TableCell align="center">{row.winnerCount ?? "-"}</TableCell>
                            <TableCell align="center">{statusLabel(row)}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                                {/* Every tournament can be edited, even after payout. */}
                                <Tooltip title="Edit">
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={{ color: tokens.textDim }}
                                      onClick={() => {
                                        setEditData(row);
                                        setCreateOpen(true);
                                      }}
                                    >
                                      {/* {row.status !== "OPEN" ? <AiOutlineEye size={18} /> : <AiOutlineEdit size={18} />} */}
                                      <AiOutlineEdit size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={hasEnded(row) ? "Ended, so its games can no longer be changed" : "Games"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={{ color: tokens.textDim }}
                                      disabled={hasEnded(row)}
                                      onClick={() => {
                                        setSelected(row);
                                        setGamesOpen(true);
                                      }}
                                    >
                                      <SportsEsportsIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Results & payout">
                                  <IconButton
                                    size="small"
                                    sx={{ color: tokens.textDim }}
                                    onClick={() => {
                                      setSelected(row);
                                      setPlayersOpen(true);
                                    }}
                                  >
                                    <EmojiEventsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {/* Paid tournaments can be deleted too; prizes already credited stay with the players. */}
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={{ color: tokens.textDim }}
                                      onClick={() => deleteTournament(row)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {pastPageCount > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 1.5 }}>
                      <span style={{ fontSize: "13px", color: tokens.textDim }}>Past tournaments</span>
                      <Pagination
                        count={pastPageCount}
                        page={pastPage}
                        onChange={(event, page) => setPastPage(page)}
                        size="small"
                      />
                    </Box>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tournament;
